import React, { useMemo } from 'react';
import { getDailyChallengesData } from '../data/dailyChallengesData';
import { useSupabaseData } from '../hooks/useSupabaseData';

const DailyChallengesPage = ({ language = 'traditional', onBack, onLeaderboardClick, onAchievementsClick, onChallengeStart }) => {
  const { data: remote } = useSupabaseData({
    table: 'eng_dailychallenges',
    mapRow: (row) => row,
    getFallback: () => null
  });

  const challengesData = useMemo(() => {
    console.log('DailyChallengesPage - remote data:', remote);
    
    if (!remote || (Array.isArray(remote) && remote.length === 0)) {
      console.log('DailyChallengesPage - Using fallback data');
      return getDailyChallengesData();
    }
    
    const rows = Array.isArray(remote) ? remote : [remote];
    const base = {
      title: { traditional: '每日挑戰', simplified: '每日挑战', english: 'Daily Challenge' },
      streakCard: { streak: 0, label: { traditional: '天連擊', simplified: '天连击', english: 'Day Streak' }, message: { traditional: '', simplified: '', english: '' } },
      userStats: [],
      todayChallenges: { title: { traditional: '今日挑戰', simplified: '今日挑战', english: "Today's Challenges" }, challenges: [] },
      bottomActions: []
    };
    
    rows.forEach((r) => {
      const dataType = r.data_type;
      console.log('DailyChallengesPage - processing row:', dataType, r);
      
      if (dataType === 'daily_challenge') {
        base.title = {
          traditional: r.challenge_title_traditional || base.title.traditional,
          simplified: r.challenge_title_simplified || base.title.simplified,
          english: r.challenge_title_english || base.title.english
        };
      } else if (dataType === 'streak_card') {
        base.streakCard = {
          streak: r.streak_days ?? 0,
          label: { 
            traditional: r.streak_label_traditional || '天連擊', 
            simplified: r.streak_label_simplified || '天连击', 
            english: r.streak_label_english || 'Day Streak' 
          },
          message: { 
            traditional: r.streak_message_traditional || '', 
            simplified: r.streak_message_simplified || '', 
            english: r.streak_message_english || '' 
          }
        };
      } else if (dataType === 'user_stat') {
        base.userStats.push({
          id: r.stat_id || r.id,
          value: r.stat_value,
          label: { 
            traditional: r.stat_label_traditional || '', 
            simplified: r.stat_label_simplified || '', 
            english: r.stat_label_english || '' 
          },
          color: r.stat_color || 'text-white'
        });
      } else if (dataType === 'today_challenge' && r.challenge_id) {
        base.todayChallenges.challenges.push({
          id: r.challenge_id,
          title: { 
            traditional: r.individual_challenge_title_traditional || '', 
            simplified: r.individual_challenge_title_simplified || '', 
            english: r.individual_challenge_title_english || '' 
          },
          description: { 
            traditional: r.challenge_description_traditional || '', 
            simplified: r.challenge_description_simplified || '', 
            english: r.challenge_description_english || '' 
          },
          reward: r.challenge_reward ?? 0,
          isCompleted: Boolean(r.challenge_is_completed),
          statusIcon: r.challenge_status_icon || (r.challenge_is_completed ? '✓' : '🎯'),
          statusColor: r.challenge_status_color || (r.challenge_is_completed ? 'text-green-400' : 'text-blue-400')
        });
      } else if (dataType === 'bottom_action') {
        base.bottomActions.push({
          id: r.action_id || r.id,
          title: { 
            traditional: r.action_title_traditional || '', 
            simplified: r.action_title_simplified || '', 
            english: r.action_title_english || '' 
          },
          icon: r.action_icon || '🎯',
          gradient: r.action_gradient || 'from-purple-500 to-pink-500'
        });
      }
    });
    
    console.log('DailyChallengesPage - processed data:', base);
    return base;
  }, [remote]);

  const getText = (textObj) => {
    return textObj[language] || textObj.traditional;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      {/* Header */}
      <div className="px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold">
            {getText(challengesData.title)}
          </h1>
          <button onClick={onBack} className="text-white text-xl">
            ✕
          </button>
        </div>

        {/* Streak Card */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-6 mb-6">
          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <span className="text-3xl mr-2">⚡</span>
              <span className="text-4xl font-bold">{challengesData.streakCard.streak}</span>
            </div>
            <div className="text-lg font-semibold mb-2">
              {getText(challengesData.streakCard.label)}
            </div>
            <div className="text-sm opacity-90">
              {getText(challengesData.streakCard.message)}
            </div>
          </div>
        </div>

        {/* User Statistics */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {challengesData.userStats.map((stat) => (
            <div key={stat.id} className="bg-gray-800/30 border border-gray-600 rounded-xl p-4 text-center">
              <div className={`text-2xl font-bold mb-1 ${stat.color}`}>
                {stat.value}
              </div>
              <div className="text-sm text-gray-300">
                {getText(stat.label)}
              </div>
            </div>
          ))}
        </div>

        {/* Today's Challenges */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-4">
            {getText(challengesData.todayChallenges.title)}
          </h2>
          
          <div className="space-y-3">
            {challengesData.todayChallenges.challenges.map((challenge) => (
              <div key={challenge.id} className="bg-gray-800/30 border border-gray-600 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1">
                    <div className={`text-xl ${challenge.statusColor}`}>
                      {challenge.statusIcon}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white mb-1">
                        {getText(challenge.title)}
                      </h3>
                      <p className="text-gray-400 text-sm">
                        {getText(challenge.description)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-1">
                      <span className="text-yellow-400">⭐</span>
                      <span className="text-sm font-medium">{challenge.reward} XP</span>
                    </div>
                    
                    {!challenge.isCompleted && (
                      <button
                        onClick={() => onChallengeStart(challenge.id)}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg transition-colors"
                      >
                        {getText({ traditional: '開始', simplified: '开始', english: 'Start' })}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Action Buttons */}
        <div className="grid grid-cols-2 gap-4">
          {challengesData.bottomActions.map((action) => (
            <button
              key={action.id}
              onClick={() => {
                if (action.id === 'leaderboard') {
                  onLeaderboardClick();
                } else if (action.id === 'achievements') {
                  onAchievementsClick();
                }
              }}
              className={`bg-gradient-to-r ${action.gradient} hover:opacity-90 text-white font-semibold py-4 px-6 rounded-xl transition-all`}
            >
              <div className="text-center">
                <div className="text-2xl mb-2">{action.icon}</div>
                <div className="text-sm">
                  {getText(action.title)}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DailyChallengesPage;
